/**
 * Dashboard composite KPI hook (C4, #22328) — replaces the interim
 * dev-bypass-only `lib/auth/dev_dashboard.ts` (deleted) the same way C2's
 * `use_employees.ts` replaced `dev_employees.ts` and C3's `use_pay_runs.ts`
 * replaced `dev_pay_runs.ts`: a self-fetching hook, NOT a
 * `dev_*.ts`/`useDevBackendData` boot-time seed — see `use_my_pay.ts`'s
 * module docblock for the same "why": this is real production OAuth-gated
 * read work, live in every environment (including production), not just
 * local dev.
 *
 * Composes FOUR independent real sources behind ONE shared entity context
 * (`useOAuthEntityContext()`, resolved exactly once here — never re-resolved
 * per source; the predecessor epic found and fixed the independent
 * context-resolution bug four times already across C3/C4a/C4b/C4c of that
 * epic, see `dev_pay_run_audit.ts`'s docblock for the full history, and this
 * hook must not reintroduce it):
 *
 * - Employee records — reuses `use_employees.ts`'s exported
 *   `fetchEmployeeRecords` (never re-derives its own employee-fetch logic).
 * - Pay-run records — reuses `use_pay_runs.ts`'s exported
 *   `fetchPayRunRecords` (never re-derives its own pay-run-fetch logic).
 * - The entity's statutory filing calendar (`filingCalendar.calendar`) — NEW
 *   in this pass.
 * - The entity-wide activity feed (`audit.list`, paginated) — NEW in this
 *   pass.
 *
 * `deriveDashboardSummary`/`deriveJoinersMissingBankCount`
 * (`wagepro_store_dashboard.ts`) are UNCHANGED — this hook is simply a new
 * caller that feeds them real, self-fetched inputs instead of the
 * `wagepro_store.tsx` boot-time seed props those functions used to be fed
 * through (now removed from the store entirely — see that file's own
 * module docblock).
 *
 * ── No mock fallback, ever displayed ────────────────────────────────────────
 *
 * `deriveDashboardSummary`'s own (unchanged) contract falls back to its
 * `mock` argument whenever a given real input is `undefined` — that
 * happens both while a source is still loading AND when it has genuinely
 * failed. This hook does NOT rely on that fallback for display: it tracks
 * each of the four sources' own `loading`/`error`/`ready` status
 * independently (`TileLoadStatus`) and the screen (`dashboard_screen.tsx`)
 * renders an explicit unavailable/error state for any tile whose status
 * isn't `ready`, instead of the composed (mock-backed) number. The overall
 * hook state stays `loading` until ALL FOUR sources have settled (mirrors
 * `main.tsx`'s old `useDevBackendData`'s "settled" gating) specifically so a
 * user never sees a flash of `MOCK_DASHBOARD`'s illustrative numbers before
 * the real fetches resolve.
 *
 * Partial degradation is real and expected: an entity can have its
 * `payroll` add-on granted (employees/pay-runs resolve) while missing the
 * `reports_access` RBAC permission (filing calendar fails), or vice versa —
 * these are independent gates on independent procedures (see
 * `fetchFilingCalendar`/`fetchDashboardActivity`'s own docblocks). One
 * source failing must not collapse the WHOLE dashboard to a single global
 * error screen; each tile shows its own state.
 *
 * `activity_now` — the reference instant `ActivityFeedCard`'s
 * relative-timestamp column is rendered against — is captured once real
 * data has resolved (a genuine `new Date().toISOString()` reading, taken
 * the moment all four fetches settle), never `MOCK_NOW`. A real
 * pay-run-created event timestamped "today" must never be measured against
 * a clock frozen in the mock's own past (the exact bug this hook's
 * predecessor, C5 #21936, already fixed once for the dev-bypass path —
 * preserved here for the real path).
 *
 * `expenses_pending`/`joiners_missing_bank_details` action items: the
 * dashboard fires real, narrow `expenseClaim.list` and
 * `payroll.listLeaveRequests` fetches alongside its four KPI-tile sources
 * (issue #24350). The nav-bell badge is intentionally hidden entirely for
 * the same reason it can't just read the dashboard's total: no cheap
 * cross-screen count endpoint exists, and firing this hook's 4-source
 * fan-out on every screen would be prohibitively expensive. The badge
 * (and a matching `usePendingApprovals`-shaped hook) return once a cheap
 * count endpoint lands — issue #24350's follow-up. A gated
 * `expenseClaim.list` on Lite is classified as `entitlement_required`
 * BEFORE the surrounding `console.error` so a Lite tenant's dashboard
 * doesn't log an "error" every load, and the pending-approvals tile
 * renders an explicit "excludes expense claims (plan)" caption
 * (`approvals: 'expenses_gated'`) instead of silently under-counting. A
 * `payroll.listLeaveRequests` FORBIDDEN on a non-payroll-add-on entity is
 * classified the same way, so its dashboard load doesn't emit a
 * console.error every time either. Action items with a real count of `0`
 * are still dropped entirely by `resolveDashboardState`.
 *
 * The update-requests half of `pending_approvals` still has no real
 * backend, so it contributes `0` (never `MOCK_UPDATE_REQUESTS`'s in-review
 * rows -- previously the source of the "Pending approvals 3" bug on a
 * tenant with zero pending expenses and one real pending leave request).
 */
import * as React from 'react';
import { useWageProStore } from './wagepro_store.js';
import {
  getTrpcClient,
  isPayrollAddonRequiredError,
  isPlanGateError,
  isSubscriptionGateError,
} from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { fetchEmployeeRecords } from './use_employees.js';
import { fetchPayRunRecords } from './use_pay_runs.js';
import {
  MOCK_DASHBOARD,
  MOCK_JOINERS_MISSING_BANK_COUNT,
  MOCK_NOTIFICATIONS,
  MOCK_NOW,
} from './mock_data.js';
import {
  deriveComplianceCounts,
  deriveDashboardSummary,
  deriveJoinersMissingBankCount,
  derivePendingExpenseCount,
  derivePendingLeaveCount,
  type ComplianceCounts,
  type RealActivityAuditRow,
  type RealFilingPeriod,
} from './wagepro_store_dashboard.js';
import type { RealEmployeeRecord } from './wagepro_store_employees.js';
import type { RealPayRunRecord } from './wagepro_store_pay_runs.js';
import type { ActionItem, DashboardSummary, NotificationItem } from './types';
import type { Route } from '../lib/router.js';

// ── Filing calendar fetch (NEW, C4) ─────────────────────────────────────────

/** Input the real fetch needs to identify the entity and its due-date window — same shape `filingCalendar.calendar` accepts on the wire. */
export interface FilingCalendarFetchInput {
  firm_id: string;
  entity_id: string;
  range_start: string;
  range_end: string;
  reference_date: string;
}

/**
 * The one `filingCalendar.calendar` call {@link fetchFilingCalendar} needs,
 * isolated behind an injectable seam — mirrors `use_pay_runs.ts`'s
 * `PayRunsFetchDeps` / `defaultPayRunsFetchDeps()` DI pattern.
 */
export interface FilingCalendarFetchDeps {
  getFilingCalendar: (
    input: FilingCalendarFetchInput,
  ) => Promise<{ periods: RealFilingPeriod[] }>;
}

function defaultFilingCalendarFetchDeps(): FilingCalendarFetchDeps {
  const client = getTrpcClient();
  return {
    getFilingCalendar: (input) => client.filingCalendar.calendar.query(input),
  };
}

export type FilingCalendarFetchResult =
  | { outcome: 'success'; periods: RealFilingPeriod[] }
  | { outcome: 'failure' };

/**
 * Resolves the entity's real statutory filing calendar, or classifies why
 * it couldn't. `filingCalendar.calendar` is gated by the `reports_access`
 * RBAC permission (`requireEntityPermission`) PLUS an LK jurisdiction gate
 * (`assertLkJurisdiction`, `NOT_FOUND` for non-LK entities) — verified
 * directly against `apps/api/src/trpc/routers/filing_calendar.ts`, NOT the
 * `payroll` add-on gate `isPayrollAddonRequiredError` (`lib/trpc.ts`) checks
 * for. Neither gate has a machine-readable discriminator worth surfacing as
 * its own UI state (unlike the pay-run/employee entitlement_required case)
 * — this hook collapses every failure mode (FORBIDDEN, NOT_FOUND, network)
 * to one generic `failure` outcome, exactly like the deleted
 * `dev_dashboard.ts`'s `resolveDevFilingCalendar` did.
 *
 * `deps` defaults to `undefined` (never `defaultFilingCalendarFetchDeps()`
 * evaluated eagerly as the parameter's own default value) and is resolved
 * INSIDE the `try` below — a default-parameter expression runs before the
 * function body, so `getTrpcClient()` throwing synchronously in there (e.g.
 * no bearer token yet) used to escape both this function's own `try` and
 * `use_dashboard.ts`'s `Promise.all`/`void load()` as an unhandled rejection
 * instead of resolving `{ outcome: 'failure' }` (adversarial review finding,
 * C4 #22328). `isCancelled` lets the caller suppress the `console.error`
 * below for a fetch whose own React effect already unmounted/re-ran by the
 * time this rejects — mirrors `use_session_facts.ts`'s/
 * `use_pay_run_audit.ts`'s `if (cancelled) return;`-before-`console.error`
 * stance, just expressed as a callback since this function (unlike those
 * hooks' own inline `catch` blocks) has no closure over the caller's
 * `cancelled` flag.
 */
export async function fetchFilingCalendar(
  input: FilingCalendarFetchInput,
  deps?: FilingCalendarFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<FilingCalendarFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultFilingCalendarFetchDeps();
    const { periods } = await resolvedDeps.getFilingCalendar(input);
    return { outcome: 'success', periods };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real statutory filing calendar:',
        err,
      );
    }
    return { outcome: 'failure' };
  }
}

/** Days `range_start` sits before `referenceDate` — comfortably wide enough to always include the current month's EPF/ETF/APIT periods (due within ~20 days of period end) whether `referenceDate` falls early or late in the current filing cycle. Mirrors the deleted `dev_dashboard.ts`'s window exactly. */
const RANGE_START_LOOKBACK_DAYS = -35;
/** Days `range_end` sits after `referenceDate` — see {@link RANGE_START_LOOKBACK_DAYS}. */
const RANGE_END_LOOKAHEAD_DAYS = 60;

/** Exported for direct unit testing (month/year rollover, leap-year Feb 29 edge cases) — see `use_dashboard.test.ts`. */
export function isoDateOffsetDays(
  referenceDate: string,
  offsetDays: number,
): string {
  const date = new Date(`${referenceDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

// ── Activity feed fetch (NEW, C4) ───────────────────────────────────────────

/** Input the real fetch needs to identify the entity — same shape `audit.list` accepts on the wire. */
export interface DashboardActivityFetchInput {
  firm_id: string;
  entity_id: string;
}

/**
 * The one `audit.list` call {@link fetchDashboardActivity} needs (called
 * repeatedly for pagination), isolated behind an injectable seam.
 */
export interface DashboardActivityFetchDeps {
  listAudit: (input: {
    firm_id: string;
    entity_id: string;
    limit: number;
    offset: number;
  }) => Promise<{ entries: RealActivityAuditRow[]; hasMore: boolean }>;
}

function defaultDashboardActivityFetchDeps(): DashboardActivityFetchDeps {
  const client = getTrpcClient();
  return {
    listAudit: (input) => client.audit.list.query(input),
  };
}

/** `audit.list`'s own `limit` ceiling (`apps/api/src/trpc/routers/audit.ts`'s input schema: `z.number().int().min(1).max(200).default(50)`) — the most rows a single page can return. */
const ACTIVITY_FEED_PAGE_SIZE = 200;

/**
 * How many pages of `audit.list` to walk, at most, looking for recognised
 * pay-run events. `audit.list` is entity-WIDE (every `entity_type`, not
 * pre-filtered to `'pay_run'` server-side), and WagePro shares its tenant DO
 * with the rest of Ledger's accounting surfaces, so on an entity with
 * meaningful invoice/bill/journal activity the most-recent page can easily
 * contain zero pay-run rows even though real pay-run history exists — the
 * fix is to keep paging, not just widen one page (mirrors the deleted
 * `dev_dashboard.ts`'s own reasoning). Bounded at 4 pages (800 rows worst
 * case) so a genuinely non-payroll-heavy entity still degrades to "no real
 * events found" in a bounded number of round trips rather than walking the
 * entire audit log.
 */
const ACTIVITY_FEED_MAX_PAGES = 4;

export type DashboardActivityFetchResult =
  | { outcome: 'success'; rows: RealActivityAuditRow[] }
  | { outcome: 'failure' };

/**
 * Resolves the entity-wide activity feed, or classifies why it couldn't.
 * `audit.list` is a plain `entityProcedure` wrapped in
 * `requireOAuthScope('payroll:employer:read', 'payroll:employer:write')` —
 * no add-on/tier/feature gate at all (verified against
 * `apps/api/src/trpc/routers/audit.ts` directly), so any failure here
 * (missing entity membership, transient network error, …) collapses to one
 * generic `failure` outcome — there is no entitlement-required case to
 * distinguish, unlike employees/pay-runs.
 *
 * `deps`/`isCancelled` follow the exact same "resolve inside `try`, guard
 * `console.error` on cancellation" contract as {@link fetchFilingCalendar} —
 * see that function's docblock for why.
 */
export async function fetchDashboardActivity(
  input: DashboardActivityFetchInput,
  deps?: DashboardActivityFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<DashboardActivityFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultDashboardActivityFetchDeps();
    const rows: RealActivityAuditRow[] = [];
    for (let page = 0; page < ACTIVITY_FEED_MAX_PAGES; page++) {
      const result = await resolvedDeps.listAudit({
        ...input,
        limit: ACTIVITY_FEED_PAGE_SIZE,
        offset: page * ACTIVITY_FEED_PAGE_SIZE,
      });
      rows.push(...result.entries);
      if (!result.hasMore) break;
    }
    return { outcome: 'success', rows };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real activity feed:',
        err,
      );
    }
    return { outcome: 'failure' };
  }
}

// ── Pending-approvals fetches (NEW, #24350) ─────────────────────────────────

/**
 * Just the pending-leave count the dashboard needs. Deliberately narrower
 * than `fetchLeaveRequestsBundle` (which also fetches `payroll.listEmployees`
 * for name resolution the tile does not care about) — the dashboard already
 * has its own separate `listEmployees` fetch through `fetchEmployeeRecords`,
 * so a second one from the leave bundle would be a wasted round-trip
 * (adversarial review finding, #24350 fixup).
 */
export interface PendingLeaveFetchInput {
  firm_id: string;
  entity_id: string;
}

export interface PendingLeaveFetchDeps {
  listLeaveRequests: (
    input: PendingLeaveFetchInput,
  ) => Promise<{ requests: { status: string }[] }>;
}

function defaultPendingLeaveFetchDeps(): PendingLeaveFetchDeps {
  const client = getTrpcClient();
  return {
    listLeaveRequests: (input) => client.payroll.listLeaveRequests.query(input),
  };
}

export type PendingLeaveFetchResult =
  | { outcome: 'success'; count: number }
  | { outcome: 'failure' };

/**
 * Resolves the entity's pending-leave count, or generic failure. Never
 * throws. A payroll-add-on gate rejection (`requirePayrollAddon` — the
 * `listLeaveRequests` router uses `payrollEntityProcedure`) is an
 * entitlement STATE, not an error condition worth logging on every
 * dashboard load — classified BEFORE the surrounding `console.error` so a
 * non-payroll-add-on tenant's dashboard doesn't emit a log entry every
 * load, exactly like the `expenseClaim.list` plan-gate case above.
 */
export async function fetchPendingLeaveCount(
  input: PendingLeaveFetchInput,
  deps?: PendingLeaveFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<PendingLeaveFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultPendingLeaveFetchDeps();
    const { requests } = await resolvedDeps.listLeaveRequests(input);
    return { outcome: 'success', count: derivePendingLeaveCount(requests) };
  } catch (err) {
    if (!isPayrollAddonRequiredError(err) && !isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real pending-leave count:',
        err,
      );
    }
    return { outcome: 'failure' };
  }
}

/**
 * Just the pending-expense count the dashboard needs. Deliberately narrower
 * than `fetchExpensesBundle` (which also fetches `listEmployees` for name
 * resolution, `listPayRuns` for period resolution, and `entityUser.me` for
 * the write-path affordance check — none of which the dashboard tile needs).
 * `entitlement_required` is classified BEFORE the surrounding `console.error`
 * so a Lite tenant's gated `expenseClaim.list` is a plan-tier state, not an
 * error condition worth logging on every dashboard load (adversarial review
 * finding, #24350 fixup).
 */
export interface PendingExpensesFetchInput {
  firm_id: string;
  entity_id: string;
}

export interface PendingExpensesFetchDeps {
  listClaims: (
    input: PendingExpensesFetchInput & {
      status: 'submitted';
      limit: number;
    },
  ) => Promise<{ claims: { status: string }[] }>;
}

function defaultPendingExpensesFetchDeps(): PendingExpensesFetchDeps {
  const client = getTrpcClient();
  return {
    listClaims: (input) => client.expenseClaim.list.query(input),
  };
}

/**
 * `expenseClaim.list`'s `limit` ceiling is 200 (input schema — see
 * `apps/api/src/trpc/routers/expense_claims.ts`) — mirrors
 * `use_expenses.ts`'s `MAX_CLAIMS_PAGE_SIZE`. Without an explicit `limit`,
 * the procedure defaults to 50 and older `submitted` claims silently drop
 * off the tile (adversarial review finding, #24350 fixup). An entity with
 * more than 200 lifetime `submitted` claims outstanding is out of scope
 * for this pass — same stance `use_expenses.ts` takes.
 */
const PENDING_EXPENSES_PAGE_SIZE = 200;

export type PendingExpensesFetchResult =
  | { outcome: 'success'; count: number }
  | { outcome: 'failure'; failure: 'entitlement_required' | 'error' };

function classifyPendingExpensesFailure(
  err: unknown,
): 'entitlement_required' | 'error' {
  return isPlanGateError(err) || isSubscriptionGateError(err)
    ? 'entitlement_required'
    : 'error';
}

/**
 * Resolves the entity's pending-expense count, or classifies why it
 * couldn't. `expenseClaim.list` is gated by the Starter+ `expense_claims`
 * plan feature; on Lite it reliably rejects with a plan-tier gate error —
 * classified here as `entitlement_required` BEFORE the surrounding
 * `console.error`, so a Lite tenant's dashboard doesn't emit a log entry
 * every time it loads (the same "don't log a gated state as an error"
 * pattern `use_expenses.ts` establishes for the queue screen).
 *
 * Server-side `status: 'submitted'` filter (the wire enum value for a
 * pre-approval claim) + `limit: 200` — asks the DB for exactly the rows
 * the tile counts rather than pulling the default 50-row most-recent
 * page and dropping older `submitted` claims silently (adversarial
 * review finding, #24350 fixup; `expense_claims.list` orders by
 * `created_at desc`, so an entity with even a handful of recent
 * approved/rejected claims can push older still-pending ones off page 1).
 */
export async function fetchPendingExpensesCount(
  input: PendingExpensesFetchInput,
  deps?: PendingExpensesFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<PendingExpensesFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultPendingExpensesFetchDeps();
    const { claims } = await resolvedDeps.listClaims({
      ...input,
      status: 'submitted',
      limit: PENDING_EXPENSES_PAGE_SIZE,
    });
    return { outcome: 'success', count: derivePendingExpenseCount(claims) };
  } catch (err) {
    const failure = classifyPendingExpensesFailure(err);
    if (failure === 'error' && !isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real pending-expenses count:',
        err,
      );
    }
    return { outcome: 'failure', failure };
  }
}

// ── Composition ──────────────────────────────────────────────────────────

/** Per-tile resolution status — every KPI/card the dashboard renders is independently gated on one of these four. */
export type TileLoadStatus = 'loading' | 'error' | 'ready';

/**
 * The pending-approvals KPI tile has an additional `expenses_gated` state
 * on top of the standard {@link TileLoadStatus}: on the Lite plan the
 * `expenseClaim.list` procedure is behind a `assertFeatureEnabled` gate.
 * The dashboard still surfaces the leave+update-requests half of the total
 * (real signal the operator can act on) but explicitly captions "excludes
 * expense claims (plan)" so the number doesn't silently under-count
 * (adversarial review finding, #24350 fixup).
 */
export type ApprovalsTileStatus = TileLoadStatus | 'expenses_gated';

export interface DashboardTileStatuses {
  /** Gates the headcount stat tile and the `joiners_missing_bank_details` action item — both derive from employee records. */
  headcount: TileLoadStatus;
  /** Gates the gross-payroll stat tile — derives from pay-run records. */
  gross_payroll: TileLoadStatus;
  /** Gates the compliance-alerts stat tile and the statutory-deadline card — both derive from the filing calendar. */
  compliance: TileLoadStatus;
  /** Gates the activity feed card — derives from the audit log (period-joined against pay-run records). */
  activity: TileLoadStatus;
  /** Gates the pending-approvals stat tile — derives from the pending-leave and pending-expenses fetches. `expenses_gated` when the Lite plan blocks `expenseClaim.list` (leave counts still shown; see {@link ApprovalsTileStatus}). */
  approvals: ApprovalsTileStatus;
}

export interface DashboardReadyState {
  status: 'ready';
  summary: DashboardSummary;
  tile_statuses: DashboardTileStatuses;
  action_items: ActionItem[];
  /** Reference instant `summary.activity`'s relative-timestamp column is rendered against — see module docblock. Real once `status: 'ready'`, never `MOCK_NOW`. */
  activity_now: string;
  /** Overdue vs. due-soon split for the compliance-alerts KPI tile caption — never a stale mock value once `tile_statuses.compliance === 'ready'`. */
  compliance_counts: ComplianceCounts;
}

export type DashboardState =
  | { status: 'loading' }
  /** The OAuth grant has no bound entity — see `oauth_entity_context.ts`. Unreachable in practice: `main.tsx`'s `AuthGate` already gates the whole app on this via `useSessionFacts()` before `<WageProStoreProvider>` (and this hook) ever mounts. Handled defensively anyway, never a crash. */
  | { status: 'no_entity' }
  | DashboardReadyState;

/**
 * Every raw input {@link resolveDashboardState} composes over.
 * `pendingLeave`/`pendingExpenses`/`openUpdateRequests` are all plain
 * numbers here (never a raw store array) so this function never needs to
 * import `count_by_status`/`ActionItem`-filtering logic itself.
 *
 * `pendingLeave` and `pendingExpenses` are real, self-fetched counts (see
 * `useDashboard()` below). `openUpdateRequests` is `0` while no real
 * update-request backend exists — issue #24350's fix explicitly stops
 * counting the mock fixture into `pending_approvals` (the caller passes a
 * literal `0` here rather than reading the store's mock rows).
 *
 * `pendingLeaveFailed` / `pendingExpensesFailed` / `pendingExpensesGated`
 * distinguish the three post-settle states of each queue fetch — a plain
 * `0` count is ambiguous otherwise (a real empty queue vs. a failed fetch
 * vs. a plan-tier gate all read as `0`, and the tile must render each
 * differently — adversarial review finding, #24350 fixup).
 */
export interface DashboardResolveInputs {
  entityContext: OAuthEntityContext;
  employeeRecords: RealEmployeeRecord[] | undefined;
  employeeRecordsFailed: boolean;
  payRunRecords: RealPayRunRecord[] | undefined;
  payRunRecordsFailed: boolean;
  filingPeriods: RealFilingPeriod[] | undefined;
  filingFailed: boolean;
  activityRows: RealActivityAuditRow[] | undefined;
  activityFailed: boolean;
  /** Real clock reading, captured once ALL FOUR fetches have settled — `undefined` until then. Never `MOCK_NOW`. See module docblock's "activity_now" section. */
  activityNow: string | undefined;
  /** "Today", `YYYY-MM-DD`, captured at the same moment as `activityNow` — threaded into `deriveDashboardSummary` rather than read from the wall clock inside this pure function, so it stays fully deterministic and testable with an arbitrary pinned date (mirrors `deriveStatutoryDeadline`'s own `referenceDate` contract). */
  referenceDate: string | undefined;
  pendingExpenses: number;
  pendingLeave: number;
  openUpdateRequests: number;
  /** `true` when `expenseClaim.list` rejected with a plan-tier gate — the queue is unreachable on the caller's plan; the tile renders the "excludes expenses (plan)" caption. */
  pendingExpensesGated: boolean;
  /** `true` when `expenseClaim.list` rejected with a non-gate error — surfaced as a tile-level error state, never silently 0. */
  pendingExpensesFailed: boolean;
  /** `true` when `payroll.listLeaveRequests` rejected — same tile-level error surfacing as `pendingExpensesFailed`. */
  pendingLeaveFailed: boolean;
}

export interface DashboardResolveResult {
  state: DashboardState;
  pending_approvals: number;
  /** Per-tile status for the pending-approvals KPI — surfaces the `expenses_gated` case the plain `pending_approvals` number can't express. */
  pending_approvals_status: ApprovalsTileStatus;
}

/**
 * Pure decision function, exported separately from the hook below so it is
 * unit-testable without rendering React (mirrors `resolvePayRunsReadState`/
 * `resolveEmployeesState`/`resolveMyPayState`'s "pure decision function +
 * thin hook" split) — this is exactly the shape C3's own adversarial review
 * (PR #22370, finding 1) established: the hook's wiring itself needs direct
 * test coverage, not just this pure function in isolation, so
 * `use_dashboard.test.ts` tests BOTH this function AND the fetch+classify
 * functions above.
 *
 * `pending_approvals` is computed unconditionally (never gated on the four
 * dashboard-tile fetches below) — see `UseDashboardResult.pending_approvals`'s
 * own doc comment on the hook below for why.
 */
export function resolveDashboardState(
  inputs: DashboardResolveInputs,
): DashboardResolveResult {
  const pending_approvals = computePendingApprovals(
    inputs.pendingLeave,
    inputs.pendingExpenses,
    inputs.openUpdateRequests,
  );

  // Approvals tile precedence: BOTH sides failed -> tile-level error.
  // Expenses gated but leave OK -> `expenses_gated` (count still shown,
  // captioned). Either side failed -> `error`. Otherwise `ready`. There is
  // no `loading` case here: the approvals tile is computed unconditionally
  // even in the global `loading` branch below, so `pending_leave` /
  // `pending_expenses` are always plain numbers by the time this runs
  // (`0` for a fetch that hasn't settled yet, same as the pre-settle
  // default) — the `TileLoadStatus`'s `'loading'` variant is intentionally
  // unreachable in the approvals row.
  const approvals_status: ApprovalsTileStatus =
    inputs.pendingLeaveFailed && inputs.pendingExpensesFailed
      ? 'error'
      : inputs.pendingExpensesGated && !inputs.pendingLeaveFailed
        ? 'expenses_gated'
        : inputs.pendingLeaveFailed || inputs.pendingExpensesFailed
          ? 'error'
          : 'ready';

  if (inputs.entityContext.status === 'no_entity') {
    return {
      state: { status: 'no_entity' },
      pending_approvals,
      pending_approvals_status: approvals_status,
    };
  }

  const headcount_status: TileLoadStatus =
    inputs.employeeRecords !== undefined
      ? 'ready'
      : inputs.employeeRecordsFailed
        ? 'error'
        : 'loading';
  const gross_payroll_status: TileLoadStatus =
    inputs.payRunRecords !== undefined
      ? 'ready'
      : inputs.payRunRecordsFailed
        ? 'error'
        : 'loading';
  const compliance_status: TileLoadStatus =
    inputs.filingPeriods !== undefined
      ? 'ready'
      : inputs.filingFailed
        ? 'error'
        : 'loading';
  // Gated on BOTH `activityRows` AND `payRunRecords` resolving -- `deriveActivity`
  // (`wagepro_store_dashboard.ts`) period-joins the audit rows against pay-run
  // records to build each event, and silently resolves `[]` (not an error) when
  // `payRunRecords` is `undefined` (see that function's own docblock). Gating
  // this tile on `activityRows` alone let a `payRunRecords` failure that
  // happened to occur alongside a SUCCESSFUL audit-log fetch render a
  // confident "ready" empty/wrong feed for an entity that may have plenty of
  // real activity (adversarial review finding, C4 #22328) -- `DashboardTileStatuses.activity`'s
  // own doc comment already named this dependency; this now actually honors it.
  const activity_status: TileLoadStatus =
    inputs.activityRows !== undefined && inputs.payRunRecords !== undefined
      ? 'ready'
      : inputs.activityFailed || inputs.payRunRecordsFailed
        ? 'error'
        : 'loading';

  const anyStillLoading =
    headcount_status === 'loading' ||
    gross_payroll_status === 'loading' ||
    compliance_status === 'loading' ||
    activity_status === 'loading';

  if (
    anyStillLoading ||
    inputs.activityNow === undefined ||
    inputs.referenceDate === undefined
  ) {
    return {
      state: { status: 'loading' },
      pending_approvals,
      pending_approvals_status: approvals_status,
    };
  }

  const summary = deriveDashboardSummary(MOCK_DASHBOARD, {
    employeeRecords: inputs.employeeRecords,
    payRunRecords: inputs.payRunRecords,
    filingPeriods: inputs.filingPeriods,
    activityRows: inputs.activityRows,
    referenceDate: inputs.referenceDate,
  });

  // Items with a real count of 0 are DROPPED entirely (issue #24350's
  // finding 5: a "0 expenses pending approval" row with a Review button is
  // noise, not action).
  const action_items: ActionItem[] = [];
  if (inputs.pendingExpenses > 0) {
    action_items.push({
      kind: 'expenses_pending',
      count: inputs.pendingExpenses,
      tone: 'warning',
    });
  }
  // Only surface the joiners-missing-bank action item once the real
  // employee records it derives from have actually resolved AND at least
  // one real match exists -- a stale mock count for a failed fetch would
  // misrepresent a real payroll gap as either present or absent when
  // neither is actually known yet (same "explicit per-tile degradation"
  // stance the stat tiles take above), and a real count of 0 is dropped
  // for the same reason as expenses above.
  if (headcount_status === 'ready') {
    const joinersCount = deriveJoinersMissingBankCount(
      MOCK_JOINERS_MISSING_BANK_COUNT,
      inputs.employeeRecords,
    );
    if (joinersCount > 0) {
      action_items.push({
        kind: 'joiners_missing_bank_details',
        count: joinersCount,
        tone: 'danger',
      });
    }
  }

  return {
    state: {
      status: 'ready',
      summary,
      tile_statuses: {
        headcount: headcount_status,
        gross_payroll: gross_payroll_status,
        compliance: compliance_status,
        activity: activity_status,
        approvals: approvals_status,
      },
      action_items,
      activity_now: inputs.activityNow,
      compliance_counts: deriveComplianceCounts(inputs.filingPeriods ?? []),
    },
    pending_approvals,
    pending_approvals_status: approvals_status,
  };
}

/**
 * Sum of the three "awaiting a decision" queues the dashboard's Pending
 * Approvals KPI tile and the nav-bell badge share. Every input is a plain
 * number so callers with different data-fetch strategies (the dashboard
 * fetches real leave/expense counts; the nav bell doesn't fetch at all)
 * pass exactly what they have without importing status-filtering helpers.
 *
 * Any queue with no real source contributes `0` (issue #24350's finding 2:
 * `MOCK_UPDATE_REQUESTS`'s three in-review rows must never leak into the
 * total). Gated queues (e.g. expenses when the plan is Lite) also
 * contribute `0`, exactly like a queue with no rows.
 */
export function computePendingApprovals(
  pendingLeave: number,
  pendingExpenses: number,
  openUpdateRequests: number,
): number {
  return pendingLeave + pendingExpenses + openUpdateRequests;
}

/**
 * Where the dashboard's pending-approvals "Review" link should land: the
 * first approval queue that actually has something waiting, so the employer
 * lands on the item itself rather than an empty tab. Leave comes first
 * (time-sensitive — the absence is usually imminent), then expenses.
 * `null` when nothing is pending, so no link renders.
 *
 * Update requests are not a destination yet: their count is always `0`
 * until that screen has a real backend (see `useDashboard()`).
 */
export function pendingApprovalsRoute(
  pendingLeave: number,
  pendingExpenses: number,
): Route | null {
  if (pendingLeave > 0) return { module: 'leave', tab: 'pending' };
  if (pendingExpenses > 0) return { module: 'expenses', tab: 'pending' };
  return null;
}

export interface UseDashboardResult {
  state: DashboardState;
  /**
   * Total items awaiting a decision. Deliberately hoisted OUTSIDE `state` so
   * `DashboardScreen`'s pending-approvals KPI tile can render a partial
   * total (leave only) even while its own tile status is `expenses_gated`
   * or `error` — those states carry a caption, not a hidden number.
   */
  pending_approvals: number;
  /** Per-tile status for the pending-approvals KPI — surfaces the `expenses_gated` / `error` cases the plain `pending_approvals` number can't express (see {@link ApprovalsTileStatus}). */
  pending_approvals_status: ApprovalsTileStatus;
  /** Approval queue the tile's "Review" link opens — see {@link pendingApprovalsRoute}. `null` when nothing is pending. */
  pending_approvals_route: Route | null;
  /** Re-runs every fetch this hook owns. Meaningful whenever any tile shows `error` (e.g. a transient network failure, or an entitlement that was just granted). */
  retry: () => void;
}

/**
 * Dashboard tiles, action list and activity feed — the composite KPI view
 * (C4, #22328). See module docblock for the full "how four independent real
 * sources compose into one screen, without a global error state masking
 * partial success" story. `resolveDashboardState` above is the pure decision
 * function; this hook is a thin caller that owns the React state/effect
 * fan-out and calls it with the real fetch results.
 *
 * The `update_requests_open` and `permits_expiring` action items stay
 * dropped entirely from `action_items` (not wired to a permanent literal
 * `0` row) — both depend on backend concepts that do not exist (a
 * change-request backend, and any document-expiry concept respectively —
 * see the epic's own non-goals list). `pending_approvals` on the dashboard
 * tile IS now real — see the module docblock's "expenses_pending /
 * joiners_missing_bank_details" section. The nav-bell badge total stays
 * hidden entirely until a cheap count endpoint exists — see this module's
 * own docblock's "nav-bell badge" note.
 */
export function useDashboard(): UseDashboardResult {
  const entityContext: OAuthEntityContext = useOAuthEntityContext();
  const { session } = useWageProStore();

  const [employeeRecords, setEmployeeRecords] = React.useState<
    RealEmployeeRecord[] | undefined
  >(undefined);
  const [employeeRecordsFailed, setEmployeeRecordsFailed] =
    React.useState(false);
  const [payRunRecords, setPayRunRecords] = React.useState<
    RealPayRunRecord[] | undefined
  >(undefined);
  const [payRunRecordsFailed, setPayRunRecordsFailed] = React.useState(false);
  const [filingPeriods, setFilingPeriods] = React.useState<
    RealFilingPeriod[] | undefined
  >(undefined);
  const [filingFailed, setFilingFailed] = React.useState(false);
  const [activityRows, setActivityRows] = React.useState<
    RealActivityAuditRow[] | undefined
  >(undefined);
  const [activityFailed, setActivityFailed] = React.useState(false);
  // Pending leave / expense counts derived from the real backend fetches
  // fired below. `undefined` means "not settled yet"; a gated or failed
  // fetch resolves to `0` alongside the tri-state flags below so the tile
  // can render each case distinctly (an "excludes expenses (plan)" caption
  // on gate, an error state on failure, a plain "N" on success).
  const [pendingLeave, setPendingLeave] = React.useState<number>(0);
  const [pendingLeaveFailed, setPendingLeaveFailed] =
    React.useState<boolean>(false);
  const [pendingExpenses, setPendingExpenses] = React.useState<number>(0);
  const [pendingExpensesFailed, setPendingExpensesFailed] =
    React.useState<boolean>(false);
  const [pendingExpensesGated, setPendingExpensesGated] =
    React.useState<boolean>(false);
  // Real clock reading, captured once ALL FOUR fetches below have settled --
  // never a `MOCK_NOW` fallback, never re-read on every render. See module
  // docblock's "activity_now" section.
  const [activityNow, setActivityNow] = React.useState<string | undefined>(
    undefined,
  );
  const [referenceDate, setReferenceDate] = React.useState<string | undefined>(
    undefined,
  );
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    // Captured as its own consts so the nested async closure below
    // references definitely-string values rather than relying on narrowing
    // of `entityContext` surviving across the closure boundary -- same
    // pattern `use_session_facts.ts`/`use_my_pay.ts`/`use_pay_runs.ts` use.
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    const todayIso = new Date().toISOString().slice(0, 10);
    let cancelled = false;
    setEmployeeRecords(undefined);
    setEmployeeRecordsFailed(false);
    setPayRunRecords(undefined);
    setPayRunRecordsFailed(false);
    setFilingPeriods(undefined);
    setFilingFailed(false);
    setActivityRows(undefined);
    setActivityFailed(false);
    setPendingLeave(0);
    setPendingLeaveFailed(false);
    setPendingExpenses(0);
    setPendingExpensesFailed(false);
    setPendingExpensesGated(false);

    async function load(): Promise<void> {
      // All six fetches share the SAME `firmId`/`entityId` resolved once
      // above from `entityContext` -- never independently re-resolved (see
      // module docblock's "one shared entity context" section). Each fetch
      // function never throws (every failure mode is caught and classified
      // internally), so `Promise.all` here is safe -- a rejection from one
      // can never abort the others.
      // `() => cancelled` is passed to the four fetches that read this
      // closure's own `isCancelled` seam (see their docblocks) so a
      // console.error for a genuinely-cancelled fetch (this effect re-ran or
      // unmounted before the promise settled) is suppressed the same way
      // `use_session_facts.ts`/`use_pay_run_audit.ts` guard their own inline
      // catch blocks. `fetchPayRunRecords` is untouched (a separate, already
      // shipped module -- out of this pass's scope).
      //
      // The pending-leave and pending-expenses fetches go through
      // dedicated, narrow helpers (`fetchPendingLeaveCount`,
      // `fetchPendingExpensesCount`) rather than the queue-screen bundles
      // (`fetchLeaveRequestsBundle` / `fetchExpensesBundle`) -- the bundles
      // pull `listEmployees`/`listPayRuns`/`entityUser.me` that the tile
      // does not need, tripling the fan-out for nothing (adversarial
      // review finding, #24350 fixup).
      const [
        employeesResult,
        payRunsResult,
        filingResult,
        activityResult,
        expensesResult,
        leaveResult,
      ] = await Promise.all([
        fetchEmployeeRecords(
          { firm_id: firmId, entity_id: entityId },
          undefined,
          () => cancelled,
        ),
        fetchPayRunRecords({ firm_id: firmId, entity_id: entityId }),
        fetchFilingCalendar(
          {
            firm_id: firmId,
            entity_id: entityId,
            range_start: isoDateOffsetDays(todayIso, RANGE_START_LOOKBACK_DAYS),
            range_end: isoDateOffsetDays(todayIso, RANGE_END_LOOKAHEAD_DAYS),
            reference_date: todayIso,
          },
          undefined,
          () => cancelled,
        ),
        fetchDashboardActivity(
          { firm_id: firmId, entity_id: entityId },
          undefined,
          () => cancelled,
        ),
        fetchPendingExpensesCount(
          { firm_id: firmId, entity_id: entityId },
          undefined,
          () => cancelled,
        ),
        fetchPendingLeaveCount(
          { firm_id: firmId, entity_id: entityId },
          undefined,
          () => cancelled,
        ),
      ]);
      if (cancelled) return;

      if (employeesResult.outcome === 'success') {
        setEmployeeRecords(employeesResult.records);
      } else {
        setEmployeeRecordsFailed(true);
      }
      if (payRunsResult.outcome === 'success') {
        setPayRunRecords(payRunsResult.records);
      } else {
        setPayRunRecordsFailed(true);
      }
      if (filingResult.outcome === 'success') {
        setFilingPeriods(filingResult.periods);
      } else {
        setFilingFailed(true);
      }
      if (activityResult.outcome === 'success') {
        setActivityRows(activityResult.rows);
      } else {
        setActivityFailed(true);
      }
      if (expensesResult.outcome === 'success') {
        setPendingExpenses(expensesResult.count);
      } else if (expensesResult.failure === 'entitlement_required') {
        setPendingExpensesGated(true);
      } else {
        setPendingExpensesFailed(true);
      }
      if (leaveResult.outcome === 'success') {
        setPendingLeave(leaveResult.count);
      } else {
        setPendingLeaveFailed(true);
      }
      setReferenceDate(todayIso);
      setActivityNow(new Date().toISOString());
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  // Memoized on the actual raw inputs `resolveDashboardState` (plus the
  // `company_name` override below) reads, so a re-render triggered by
  // something entirely unrelated -- `App.tsx`'s global search box state,
  // most notably: `search_query` lives there and a keystroke re-renders the
  // whole tree, which re-renders `DashboardScreen` (same component
  // instance, not remounted) -- reuses the SAME `state`/`pending_approvals`
  // object/array references instead of rebuilding the pay-run `Map` and
  // re-looping up to 800 audit rows on every keystroke and handing back
  // fresh object identities every time (which would also defeat any
  // downstream `useMemo`/`useEffect` keying on this hook's return value).
  // Mirrors `use_employees.ts`'s `useEmployeesState()` memoization, added
  // there for the exact same class of bug (adversarial review finding, C4
  // #22328).
  const resolved = React.useMemo(() => {
    const result = resolveDashboardState({
      entityContext,
      employeeRecords,
      employeeRecordsFailed,
      payRunRecords,
      payRunRecordsFailed,
      filingPeriods,
      filingFailed,
      activityRows,
      activityFailed,
      activityNow,
      referenceDate,
      pendingExpenses,
      pendingLeave,
      pendingExpensesGated,
      pendingExpensesFailed,
      pendingLeaveFailed,
      // The update-requests screen has no real backend yet
      // (`use_update_requests.ts` reads MOCK_UPDATE_REQUESTS). Issue #24350's
      // fix: contribute `0` here rather than the mock's in-review row count.
      openUpdateRequests: 0,
    });
    if (result.state.status !== 'ready') return result;
    return {
      ...result,
      state: {
        ...result.state,
        summary: {
          ...result.state.summary,
          // `session.company_name` is REAL once `useSessionFacts()` resolves
          // (`wagepro_store.tsx`'s `deriveSessionFromFacts`) -- and it always
          // has by the time this hook runs, since `main.tsx`'s `AuthGate`
          // already gates mounting `<WageProStoreProvider>`/`<App>` on that.
          // `deriveDashboardSummary`'s own `MOCK_DASHBOARD.company_name`
          // ('Ceylon Brightways (Pvt) Ltd') must never reach a real customer
          // (adversarial review finding, C4 #22328).
          company_name: session.company_name,
          // `greeting_name` — the follow-up `wagepro_store_dashboard.ts`'s
          // module docblock flagged as having "no equivalent real source
          // yet" now has one: `session.name` is real once
          // `wagepro_store_identity.ts`'s `deriveSessionFromIdentity`
          // resolves the account's actual signup `full_name` (previously it
          // only ever guessed a name from the email local-part). The
          // greeting uses first name only ("Good morning, Dilshan", not the
          // full "Dilshan Rajapaksa") — same convention `deriveSessionFromIdentity`
          // itself doesn't apply, since a full display name is correct for
          // the account menu but a first name reads more naturally here.
          greeting_name: session.name.split(/\s+/)[0] || session.name,
        },
      },
    };
  }, [
    entityContext,
    employeeRecords,
    employeeRecordsFailed,
    payRunRecords,
    payRunRecordsFailed,
    filingPeriods,
    filingFailed,
    activityRows,
    activityFailed,
    activityNow,
    referenceDate,
    pendingExpenses,
    pendingLeave,
    pendingExpensesGated,
    pendingExpensesFailed,
    pendingLeaveFailed,
    session.company_name,
    session.name,
  ]);

  // Memoized so the route object keeps its identity across renders with
  // unchanged counts, same as `resolved` above.
  const pending_approvals_route = React.useMemo(
    () => pendingApprovalsRoute(pendingLeave, pendingExpenses),
    [pendingLeave, pendingExpenses],
  );

  // Stable across renders (`useCallback`, no deps, functional state updater)
  // -- same reasoning as the `useMemo` above: a fresh closure identity on
  // every render would defeat any caller that keys its own memoization off
  // this function.
  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  return {
    state: resolved.state,
    pending_approvals: resolved.pending_approvals,
    pending_approvals_status: resolved.pending_approvals_status,
    pending_approvals_route,
    retry,
  };
}

export interface UseNotificationsResult {
  notifications: NotificationItem[];
  /** Reference instant relative timestamps are rendered against. */
  now: string;
}

/** Nav-bell notifications. Untouched by this pass — still fully mock-backed (epic non-goal, see module docblock). */
export function useNotifications(): UseNotificationsResult {
  return { notifications: MOCK_NOTIFICATIONS, now: MOCK_NOW };
}
